import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import {
  AnalyticsEventEnum,
  BehaviorCodeResponseDto,
  ReactionEntriesListResponseDto
} from '@whetstoneeducation/hero-common';
import { AppToastManagerService } from '../../../shared/services/toast-manager.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../shared/base-classes/base.component';
import { IBehaviorCodeEntryConfirmReactionsResolverData } from './behavior-code-entry-confirm-reaction-models/behavior-code-entry-confirm-reactions-resolver-data.interface';
import { ITableFilters } from '../../../shared/tables/table-filters/table-filters.interface';
import { ComplianceListColumnsEnum } from '../../compliance/compliance-list/compliance-list-models/compliance-list-column.enum';
import { AppBehaviorCodeEntryService } from '../behavior-code-entry.service';
import { MatDialog } from '@angular/material/dialog';
import { AppBehaviorCodeEntryEditReactionModalComponent } from '../behavior-code-entry-edit-reaction-modal/behavior-code-entry-edit-reaction-modal.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AppPageHeaderService } from '../../../shared/page-header/page-header.service';
import { PrinterService } from '../../../shared/printer/printer.service';
import { HeaderButtonAction } from '../../../shared/page-header/header-button';
import { printPass } from '../../../shared/printer/browser-print.util';
import { AppAnalyticsService } from '../../../shared/services/analytics.service';

export interface PrintableReactionEntry extends ReactionEntriesListResponseDto {
  canPrint: boolean;
}

@Component({
  selector: 'app-behavior-code-entry-confirm-reactions',
  templateUrl: './behavior-code-entry-confirm-reactions.component.html',
  styleUrls: ['./behavior-code-entry-confirm-reactions.component.scss']
})
export class BehaviorCodeEntryConfirmReactionsComponent
  extends BaseComponent
  implements AfterViewInit, OnDestroy {
  public reactionEntries: PrintableReactionEntry[];
  public tableFilters: ITableFilters;
  public behaviorCodeEntriesIds: number[];
  public behaviorCodes: Map<number, BehaviorCodeResponseDto>;
  public pageHeaderSubscription: Subscription;
  public browserPrint: boolean = false;
  public destroy$ = new Subject<void>();

  constructor(
    private toastService: AppToastManagerService,
    public route: ActivatedRoute,
    public router: Router,
    public behaviorCodeEntryService: AppBehaviorCodeEntryService,
    private pageHeaderService: AppPageHeaderService,
    private dialog: MatDialog,
    private printer: PrinterService,
    private location: Location,
    private analyticsService: AppAnalyticsService
  ) {
    super();
    this.loadData();
    this.pageHeaderService.buttonAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (action: string) => {
        if (action === HeaderButtonAction.BACK) {
          this.location.back();
        }
      });
  }
  async ngAfterViewInit(): Promise<void> {
    const printableReactionEntries = this.reactionEntries.filter(
      (reactionEntry) => reactionEntry.canPrint
    );

    if (!printableReactionEntries.length) {
      return;
    }

    const dialog = await this.printer.initializeDevice();
    printableReactionEntries.forEach(reactionEntry => {
      if (dialog) {
        dialog.afterClosed().subscribe((browserPrint) => {
          this.browserPrint = browserPrint;
          this.printSingleReaction(reactionEntry);
        });
      } else {
        this.printSingleReaction(reactionEntry);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadData(): void {
    this.isLoading = true;
    const data = this.route.snapshot.data
      .data as IBehaviorCodeEntryConfirmReactionsResolverData;
    this.tableFilters = data.tableFilters;
    this.behaviorCodeEntriesIds = data.behaviorCodeEntriesIds;
    this.behaviorCodes = data.behaviorCodes;
    this.reactionEntries = this.addCanPrintFlag(data.reactionEntries.results);

    const behaviorCodeName = this.reactionEntries[0]?.behaviorCodeName;

    this.pageHeaderService.changeTitle(`Confirm Reactions`);

    if (behaviorCodeName) {
      this.pageHeaderService.changeSubtitle(`${behaviorCodeName}`);
    }
  }

  private async printSingleReaction(reactionEntry: ReactionEntriesListResponseDto) {
    if (this.printer.usbAvailable) {
      try {
        
        const printTrack = await this.behaviorCodeEntryService.generatePrintData(
          this.printer.configuration.languageType,
          reactionEntry
        );
        
        for (let i = 0; i < reactionEntry.printCopies; i++) {
          this.printer.addToPrintQueue(printTrack);
        }

      } catch (error) {
        this.toastService.error('Error printing reaction, printer not found');
      }
    } else if (!this.printer.usbAllowed || this.browserPrint) {
      printPass(reactionEntry);
      this.analyticsService.track(
        AnalyticsEventEnum.PASS_PRINTED,
        reactionEntry.id
      );
    }
  }

  protected readonly columns = ComplianceListColumnsEnum;

  public get displayedColumns() {
    return Object.values(ComplianceListColumnsEnum);
  }

  public async updateTableFilters(
    newTableFilters: ITableFilters
  ): Promise<void> {
    this.isLoading = true;
    this.tableFilters = newTableFilters;
    await this.updateReactionEntries(this.tableFilters);
    this.isLoading = false;
  }

  public async updateReactionEntries(filters: ITableFilters): Promise<void> {
    const schoolId = this.StorageManager.getLoggedInUser().currentSchoolId;
    const response =
      await this.behaviorCodeEntryService.getBehaviorCodeEntriesByIds(
        this.behaviorCodeEntriesIds,
        schoolId,
        filters,
        true
      );
    this.reactionEntries = this.addCanPrintFlag(response.results);
  }

  public async reloadTable(): Promise<void> {
    this.isLoading = true;
    await this.updateReactionEntries(this.tableFilters);
    this.isLoading = false;
  }

  public openEditReactionModal(
    reactionEntry: ReactionEntriesListResponseDto
  ): void {
    this.dialog.open(AppBehaviorCodeEntryEditReactionModalComponent, {
      data: {
        reactionEntry,
        onClose: async () => {
          await this.reloadTable();
          this.dialog.closeAll();
        }
      },
      width: '500px',
      height: 'auto'
    });
  }

  private addCanPrintFlag(
    reactionEntries: ReactionEntriesListResponseDto[]
  ): PrintableReactionEntry[] {
    return reactionEntries.map((reactionEntry) => {
      const behaviorCode = this.behaviorCodes.get(reactionEntry.behaviorCodeId);
      return Object.assign(reactionEntry, {
        canPrint:
          this.behaviorCodeEntryService.printsPass(reactionEntry, behaviorCode) &&
          (reactionEntry.printCopies ?? 0) > 0
      });
    });
  }
}
